import React, { useEffect, useMemo, useState } from 'react';
import apis from '@src/apis';
import { useMediaQuery } from '@mui/material';
import { LANGUAGE } from '@src/constants';
import theme from '@src/styles/theme';
import { SORT_VOICE_OPTIONS_FAVOURITE } from '@src/constants/voice';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  LIMIT_GET_DETAIL_LEADERBOARD_EVENT,
  TYPES,
} from '@src/constants/leaderboard';
import useHandleFavouriteVoices from '@src/hooks/useHandleFavouriteVoices';
import {
  dateInLastDays,
  getTopVoicesAndRankingListVoices,
} from '@src/services/leaderboard';
import {
  StyledDivider,
  StyledRewardContainer,
  StyledVoiceLeaderboardContainer,
} from './index.style';
import Header from './Header';
import Podium from './Podium';
import RankingList from './RankingList';
import Reward from './Reward';
import Rules from './Rules';
import MobileWebLeaderboard from './MobileWeb';

const VoiceLeaderboardContainer = () => {
  const history = useHistory();
  const { i18n } = useTranslation();
  const { language } = i18n;
  const isMobile = useMediaQuery(theme.breakpoints.down('smd'));
  const [favouriteVoiceCodes, setFavouriteVoiceCodes] = useState([]);
  const [activeTab, setActiveTab] = useState(TYPES[0].value);
  const [currentEvent, setCurrentEvent] = useState();
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);
  const [nextEvent, setNextEvent] = useState();
  const [eventShow, setEventShow] = useState();
  const [previousEvent, setPreviousEvent] = useState();
  const [voicesDetailData, setVoicesDetailData] = useState();
  const [loadingVoiceDetail, setLoadingVoiceDetail] = useState(false);

  const displayDurationDays = Math.max(
    ...(eventShow?.prizes?.trending || []).map(
      (item) => item?.badge?.displayDurationDays,
    ),
    ...(eventShow?.prizes?.emerging || []).map(
      (item) => item?.badge?.displayDurationDays,
    ),
  );

  const fetchFavouriteVoices = async () => {
    const data = await apis.favouriteVoices.getFavouriteVoices({
      sort: SORT_VOICE_OPTIONS_FAVOURITE[0].value,
    });
    if (data?.status) {
      setFavouriteVoiceCodes(
        data.result?.favouriteVoices?.map((voice) => voice.code) || [],
      );
    }
  };

  const fetchDetailLeaderboardEvent = async (eventId) => {
    setLoadingVoiceDetail(true);
    try {
      const data = await apis.leaderboard.getDetailLeaderboardEvent(
        eventId,
        LIMIT_GET_DETAIL_LEADERBOARD_EVENT,
      );
      if (data?.status) {
        setVoicesDetailData(data.result);
      }
    } catch (error) {
      // Optionally handle error here, e.g. log or show notification
      // console.error('Failed to fetch leaderboard event details:', error);
    } finally {
      setLoadingVoiceDetail(false);
    }
  };

  const handleChangeTab = (tab) => {
    setActiveTab(tab);
  };

  const fetchLeaderboard = async () => {
    setLoadingLeaderboard(true);
    const data = await apis.leaderboard.getLeaderboard();
    if (data?.status) {
      let event = data.result.currentEvent;

      const showPreviousEvent =
        dateInLastDays(
          data.result.previousEvent?.endDate,
          displayDurationDays,
        ) &&
        !data.result.nextEvent &&
        !data.result.currentEvent;

      if (showPreviousEvent) {
        event = data.result.previousEvent;
      }

      if (
        !showPreviousEvent &&
        data.result.nextEvent &&
        !data.result.currentEvent
      ) {
        event = data.result.nextEvent;
      }

      setCurrentEvent(data.result.currentEvent);
      setEventShow(event);
      setNextEvent(data.result.nextEvent);
      setPreviousEvent(data.result.previousEvent);
    }
    setLoadingLeaderboard(false);
  };

  const {
    handleFavoriteToggleRequest,
    addToQueuePostProcessingFavouriteVoices,
    handleErrorProcessingFavouriteVoices,
  } = useHandleFavouriteVoices({ favouriteVoiceCodes });

  useEffect(() => {
    if (!eventShow?.id) return;
    fetchDetailLeaderboardEvent(eventShow?.id);
  }, [eventShow?.id]);

  useEffect(() => {
    fetchLeaderboard();
    fetchFavouriteVoices();
  }, []);

  const { topVoices, rankingListVoices } = getTopVoicesAndRankingListVoices(
    voicesDetailData,
    activeTab,
    eventShow,
  );

  const prizes = useMemo(() => {
    const backgrounds = [
      {
        rank: 1,
        background: 'linear-gradient(360deg, #F07E00 -4.55%, #F7F4CF 111.04%)',
      },
      {
        rank: 2,
        background: 'linear-gradient(360deg, #8AA8B5 -7.52%, #DCE5E8 107.52%)',
      },
      {
        rank: 3,
        background: 'linear-gradient(360deg, #8C4500 -20.17%, #D1A66E 98.75%)',
      },
    ];

    const isTrending = activeTab === 'trending';
    const prizesByTab = isTrending
      ? eventShow?.prizes?.trending
      : eventShow?.prizes?.emerging;
    const minCreditsByTab = isTrending
      ? eventShow?.rules?.trending?.prizes
      : eventShow?.rules?.emerging?.prizes;

    return prizesByTab?.map((prize) => {
      const minCredits = minCreditsByTab?.find(
        (item) => item.rank === prize.rank,
      )?.minCredits;

      const background = backgrounds.find(
        (b) => b.rank === prize.rank,
      )?.background;

      return {
        minCreditsByRank: minCredits,
        money: prize?.money,
        rank: prize?.rank,
        badge: prize?.badge,
        background,
      };
    });
  }, [eventShow, activeTab]);

  const onPreviousEvent = () => {
    setActiveTab(TYPES[0].value);
    setEventShow(previousEvent);
    fetchDetailLeaderboardEvent(previousEvent?.id);
  };

  const onResetEvent = () => {
    setActiveTab(TYPES[0].value);
    setEventShow(currentEvent || nextEvent);
    fetchDetailLeaderboardEvent(currentEvent?.id || nextEvent?.id);
  };

  const rulesDisplay = useMemo(() => {
    const isTrending = activeTab === TYPES[0].value;
    const displayText = isTrending
      ? eventShow?.rules?.trending?.displayText
      : eventShow?.rules?.emerging?.displayText;

    return displayText?.[language || LANGUAGE.VN];
  }, [eventShow?.rules, activeTab]);

  // Handle case when no event is available
  const eventInvalid =
    currentEvent === null &&
    nextEvent === null &&
    (previousEvent === null ||
      (previousEvent &&
        !dateInLastDays(previousEvent?.endDate, displayDurationDays)));

  useEffect(() => {
    if (eventInvalid) {
      history.push('/home');
    }
  }, [eventInvalid]);

  if (eventInvalid) return null;

  if (isMobile) {
    return <MobileWebLeaderboard />;
  }

  return (
    <>
      <StyledVoiceLeaderboardContainer>
        <Header
          eventShow={eventShow}
          previousEvent={previousEvent}
          nextEvent={nextEvent}
          activeTab={activeTab}
          handleChangeTab={handleChangeTab}
          isLoading={loadingLeaderboard || loadingVoiceDetail}
          onPreviousEvent={onPreviousEvent}
          onResetEvent={onResetEvent}
          currentEvent={currentEvent}
          commingSoon={nextEvent && !currentEvent}
          displayDurationDays={displayDurationDays}
          maxPublishDaysBeforeEvent={
            eventShow?.rules?.emerging?.maxPublishDaysBeforeEvent
          }
        />
        <Podium
          isLoading={loadingLeaderboard || loadingVoiceDetail}
          topVoices={topVoices}
          activeTab={activeTab}
          favouriteVoiceCodes={favouriteVoiceCodes}
          onFavoriteToggleRequest={handleFavoriteToggleRequest}
          onHandleFavoriteProcessingComplete={
            addToQueuePostProcessingFavouriteVoices
          }
          onErrorProcessingFavouriteVoices={
            handleErrorProcessingFavouriteVoices
          }
          setFavouriteVoiceCodes={setFavouriteVoiceCodes}
        />

        <RankingList
          onErrorProcessingFavouriteVoices={
            handleErrorProcessingFavouriteVoices
          }
          onFavoriteToggleRequest={handleFavoriteToggleRequest}
          onHandleFavoriteProcessingComplete={
            addToQueuePostProcessingFavouriteVoices
          }
          favouriteVoiceCodes={favouriteVoiceCodes}
          isLoading={loadingLeaderboard || loadingVoiceDetail}
          voiceList={rankingListVoices}
        />

        <StyledDivider />
      </StyledVoiceLeaderboardContainer>
      <StyledRewardContainer>
        <Reward
          isLoading={loadingLeaderboard || loadingVoiceDetail}
          prizes={prizes}
        />
        <Rules
          rulesDisplay={rulesDisplay}
          isLoading={loadingLeaderboard || loadingVoiceDetail}
        />
      </StyledRewardContainer>
    </>
  );
};

export default VoiceLeaderboardContainer;
